/**
 * Modal — спільна оболонка модалок (Aurora). Замінює дубльовані ModalShell /
 * `fixed inset-0` по сторінках. Дає єдину поведінку:
 *  - портал у body (щоб оверлей не замикався у скляній картці з backdrop-filter);
 *  - закриття по Esc і кліку на backdrop (клік усередині — не закриває);
 *    якщо у вікні вже щось вводили — спершу питає (`guard`);
 *  - блокування скролу body поки відкрито;
 *  - на телефоні — bottom-sheet (rounded-t), на sm+ — центрована картка.
 */
import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Close } from "./icons";
import { useOptionalConfirm } from "./ConfirmDialog";

// Стек відкритих модалок: Esc закриває лише верхню, а скрол body розблоковуємо
// тільки коли закрилась остання (інакше вкладена confirm-модалка зняла б
// блокування під усе ще відкритою батьківською).
const modalStack: symbol[] = [];
let savedOverflow = "";

interface ModalProps {
  open?: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  /** max-w-* Tailwind-клас картки (за замовч. — md). */
  size?: "sm" | "md" | "lg" | "xl";
  /** Прибрати стандартний padding контенту (напр. для таблиць). */
  bare?: boolean;
  /** id заголовка, який вікно малює саме (коли `title` не передано). */
  labelledBy?: string;
  /** Питати перед закриттям (фон, Esc, ✕), якщо у вікні щось вводили.
   *  За замовчуванням — так; `false` — для вікон, де поле лише пошук. */
  guard?: boolean;
}

const MAXW = { sm: "sm:max-w-sm", md: "sm:max-w-md", lg: "sm:max-w-lg", xl: "sm:max-w-5xl" };

/**
 * Замок прокрутки сторінки на час показу вікна.
 *
 * Винесено з `Modal`, бо в застосунку є саморобні портали (велике вікно
 * замовлення, вікна Пульта) — вони мусять блокувати фон так само. Доти фон
 * лишався прокручуваним, і праворуч було ДВА скроли одночасно: сторінки й
 * вікна. Колесо миші крутило то одне, то інше.
 *
 * Стек спільний із `Modal`: вкладене вікно, закриваючись, не має знімати
 * замок, поставлений зовнішнім.
 */
export function useBodyScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const id = Symbol("scroll-lock");
    if (modalStack.length === 0) savedOverflow = document.body.style.overflow;
    modalStack.push(id);
    document.body.style.overflow = "hidden";
    return () => {
      const i = modalStack.indexOf(id);
      if (i >= 0) modalStack.splice(i, 1);
      if (modalStack.length === 0) document.body.style.overflow = savedOverflow;
    };
  }, [active]);
}


/**
 * «Закрити без збереження?» — для фону, Esc і ✕ будь-якого вікна.
 *
 * Власник 01.10.2026: «як у Трелло — клік повз вікно має попереджати, а не
 * тихо закривати». Вікно саме помічає, що в ньому вводили (події input/change
 * з картки), — щоб не прокидати «брудний» стан із кожної з трьох десятків
 * форм окремо. Не вводили — закривається одразу, як і раніше. Кнопки дій
 * усередині («Зберегти», «Скасувати») цим не проходять — це свідомий вибір.
 *
 * Для саморобних вікон: `onClick={requestClose}` на фоні і `{...panelProps}`
 * на картці.
 *
 * `Select` і `Toggle` самі шлють подію `input`, тож вибір у них — теж зміна.
 */
export function useGuardedClose(onClose: () => void, guard = true) {
  const touchedRef = useRef(false);
  const confirm = useOptionalConfirm();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const requestClose = async () => {
    if (guard && touchedRef.current && confirm) {
      const ok = await confirm({
        title: "Закрити без збереження?",
        body: "Внесене у вікні буде втрачено.",
        confirmLabel: "Закрити",
        cancelLabel: "Лишитись",
        danger: true,
      });
      if (!ok) return;
    }
    closeRef.current();
  };
  const touch = () => {
    touchedRef.current = true;
  };
  return {
    requestClose: () => void requestClose(),
    panelProps: { onInput: touch, onChange: touch },
    reset: () => {
      touchedRef.current = false;
    },
  };
}

export function Modal({
  open = true,
  onClose,
  title,
  children,
  size = "md",
  bare = false,
  labelledBy,
  guard = true,
}: ModalProps) {
  const idRef = useRef<symbol>();
  const cardRef = useRef<HTMLDivElement>(null);
  // Унікальний id заголовка — модалки накладаються, а жорстка
  // константа змусила б `aria-labelledby` вести на перший збіг.
  const titleId = useId();
  // `onClose` міняється щорендеру в половини викликів (`busy ? () => {} :
  // onClose`), а в ефекті тепер є робота з фокусом. Тримаємо його в ref,
  // щоб ефект НЕ перезапускався: інакше на кожен рендер батька (опитування
  // раз на 30 с, відповідь запиту) фокус зривався з поля, у яке саме пишуть.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  const { requestClose, panelProps, reset } = useGuardedClose(onClose, guard);
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;

  useEffect(() => {
    if (!open) return;
    const id = Symbol("modal");
    idRef.current = id;
    reset();
    if (modalStack.length === 0) savedOverflow = document.body.style.overflow;
    modalStack.push(id);
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      // лише верхня модалка реагує на Esc
      if (e.key === "Escape" && modalStack[modalStack.length - 1] === id) {
        requestCloseRef.current();
      }
    };
    document.addEventListener("keydown", onKey);

    // Фокус. Без цього він лишався на кнопці ПІД вікном: з клавіатури Tab
    // ходив по сторінці позаду, Enter натискав ту саму кнопку ще раз, а
    // читач екрана взагалі не помічав, що щось відкрилось.
    const prevFocus = document.activeElement as HTMLElement | null;
    const card = cardRef.current;
    const focusables = () =>
      Array.from(
        card?.querySelectorAll<HTMLElement>(
          'button:not([disabled]),[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((el) => el.offsetParent !== null);

    // Первинний фокус — на картку, а не на першу кнопку: інакше
    // «Підтвердити» спрацьовував би від випадкового Enter, не давши
    // прочитати питання.
    //
    // АЛЕ якщо вміст сам попросив фокус (`autoFocus`), забирати його не
    // можна: у «Виклику майстра» так відкривається клавіатура планшета, і
    // без неї працівник цеху мусить окремо тикати в поле. React застосовує
    // autoFocus у фазі коміту, тобто ДО цього ефекту — тож просто питаємо,
    // чи фокус уже всередині картки.
    const already = document.activeElement;
    if (!(already && card && card.contains(already) && already !== card)) {
      // preventScroll: на телефоні картка це нижній аркуш у `position:
      // fixed`, і звичайний фокус змушує iOS підкрутити вікно — аркуш
      // від'їжджає від низу екрана.
      card?.focus({ preventScroll: true });
    }

    const onTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || modalStack[modalStack.length - 1] !== id) return;
      const list = focusables();
      if (list.length === 0) {
        e.preventDefault();
        return;
      }
      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement as HTMLElement | null;

      // Фокус міг ВИПАСТИ з картки: кнопку заблокували під час збереження
      // (браузер скидає фокус на <body>), список у `Select` малюється
      // порталом поза карткою, елемент прибрали. Доти пастка в такому стані
      // мовчала назавжди, і Tab починав ходити по сторінці ПОЗАДУ вікна.
      if (!active || !card || !card.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && (active === first || active === card)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onTab);

    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("keydown", onTab);
      // Повертаємо фокус туди, звідки прийшли — інакше після закриття він
      // падає на <body> і Tab починає обхід сторінки спочатку. Але лише
      // якщо той елемент ще існує: після «видалити» кнопка-джерело зникає
      // разом із рядком, і focus() на відірваному вузлі нічого не робить.
      if (prevFocus && prevFocus.isConnected) {
        prevFocus.focus({ preventScroll: true });
      }
      const i = modalStack.indexOf(id);
      if (i >= 0) modalStack.splice(i, 1);
      if (modalStack.length === 0) document.body.style.overflow = savedOverflow;
    };
    // `onClose` свідомо не в залежностях — він у ref (див. коментар вище).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9998] bg-black/50 sm:backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ animation: "overlayIn 200ms ease-out" }}
      onClick={requestClose}
    >
      {/* Роль і назва — НА САМІЙ картці, а не на тлі. Доти вони висіли на
          підкладці, тобто на елементі, клік по якому ЗАКРИВАЄ вікно: читач
          екрана оголошував «діалог» і пропонував активувати те, що його ж
          і згортає. Плюс фокус ми ставимо саме на картку — значить, і
          оголошуватись має вона.

          `id` заголовка унікальний (`useId`), бо вікна накладаються: два
          жорстких `modal-title` в документі змушували посилання вести на
          ПЕРШИЙ збіг, і верхнє вікно оголошувалось назвою нижнього. */}
      <div
        ref={cardRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title !== undefined ? titleId : labelledBy}
        className={`bg-white w-full ${MAXW[size]} rounded-t-2xl sm:rounded-2xl max-h-[92dvh] overflow-y-auto overflow-x-hidden overscroll-contain shadow-glassXl outline-none`}
        style={{ animation: "cardIn 250ms ease-out" }}
        onClick={(e) => e.stopPropagation()}
        {...panelProps}
      >
        {title !== undefined && (
          <header className="sticky top-0 bg-white sm:backdrop-blur border-b border-gray-200 px-5 py-3.5 flex items-center justify-between z-10">
            <h2 id={titleId} className="text-base font-bold text-aurora-ink m-0 truncate pr-2">
              {title}
            </h2>
            <button
              onClick={requestClose}
              aria-label="Закрити"
              className="text-aurora-muted hover:text-aurora-ink min-h-[44px] min-w-[44px] -mr-2 flex items-center justify-center flex-none"
            >
              <Close size={22} />
            </button>
          </header>
        )}
        {/* Низ — над смужкою жестів iPhone, а не під нею. */}
        <div className={bare ? "pb-[env(safe-area-inset-bottom)]" : "p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))]"}>{children}</div>
      </div>
    </div>,
    document.body,
  );
}
